import * as _ from 'lodash';
import * as qs from 'qs';
import type { AnyAction } from 'redux';
import type { ThunkAction } from 'redux-thunk';
import type { EmptyObject } from 'type-fest';

import { plural } from '../../../shared/helpers/plural';
import { ProjectCategoryName } from '../../../shared/projectsV2/common';
import { ENDPOINTS, isProjects, isSharedProjects } from '../../../shared/projectsV2/helpers';
import { COUNT_MAX_FE } from '../../../shared/projectsV2/request';
import * as API from '../api';
import type { LabelsResponse } from '../../../shared/labels';
import type { ProjectCategory } from '../../../shared/projectsV2/common';
import type { AnyFilter } from '../../../shared/projectsV2/filters';
import type { BulkProjectLabelQuery, ProjectsActionQuery, ProjectsQuery } from '../../../shared/projectsV2/request';
import type {
  BulkActionResponse,
  CategoriesSummary,
  ProjectsResponse,
  ReleaseGroupsResponse,
} from '../../../shared/projectsV2/response';
import type { AnySort } from '../../../shared/projectsV2/sorting';
import type { NewToast } from '../../components/Toaster/types';
import type { AwaitedThunkResponse, ReduxAction, SyncReduxAction } from '../../types';
import type { ReduxStore } from '../store';

import { getCategory, getFetchProjectsQuery, getProjectsFilterQuery, getSelectedProjectLocators } from './selectors';
import { get, removeEmptyAttributesFromObject } from './utils';
import type { HydrateStateQuery, TeamReqBody } from './types';

export const actionTypes = Object.freeze({
  RESET: 'PROJECTSV2_RESET',
  PRE_FETCH_LOADING: 'PROJECTSV2_PREFETCH_LOADING',
  SET_TITLE: 'PROJECTSV2_SET_TITLE',
  SET_FILTER: 'PROJECTSV2_SET_FILTER',
  SET_SORT: 'PROJECTSV2_SET_SORT',
  SET_PAGE: 'PROJECTSV2_SET_PAGE',
  SET_COUNT: 'PROJECTSV2_SET_COUNT',
  FETCH_PROJECTS: 'PROJECTSV2_FETCH_PROJECTS',
  FETCH_ORGANIZATION_LABELS: 'PROJECTSV2_FETCH_ORGANIZATION_LABELS',
  TOGGLE_SELECT_ALL_ON_PAGE: 'PROJECTSV2_TOGGLE_SELECT_ALL_ON_PAGE',
  SELECT_PROJECT: 'PROJECTSV2_SELECT_PROJECT',
  DESELECT_PROJECT: 'PROJECTSV2_DESELECT_PROJECT',
  SELECT_ALL_PROJECTS: 'PROJECTSV2_SELECT_ALL_PROJECTS',
  CLEAR_SELECTION: 'PROJECTSV2_CLEAR_SELECTION',
  HYDRATE_STATE: 'PROJECTSV2_HYDRATE_STATE',
  FETCH_CATEGORIES_SUMMARY: 'PROJECTSV2_FETCH_CATEGORIES_SUMMARY',
  SET_CATEGORY: 'PROJECTSV2_SET_CATEGORY',
  DELETE_PROJECTS: 'PROJECTSV2_DELETE_PROJECTS',
  REMOVE_FROM_TEAM: 'PROJECTSV2_REMOVE_FROM_TEAM',
  RESET_FILTERS: 'PROJECTSV2_RESET_FILTERS',
  SELECT_ALL_FILTERED_PROJECTS: 'PROJECTSV2_SELECT_ALL_FILTERED_PROJECTS',
  BULK_APPLY_LABEL: 'PROJECTSV2_BULK_APPLY_LABEL',
  BULK_APPLY_POLICY: 'PROJECTSV2_BULK_APPLY_POLICY',
});

export const resetState = () => ({
  type: actionTypes.RESET,
});

/*
 * This action informs the UI that a user-action has taken place,
 * and that a request is about to happen (it waits for the debounce timeout).
 * Basically the same as `PROJECTSV2_FETCH_PROJECTS_PENDING`, but separated to avoid confusion.
 */
export const preFetchLoading = () => ({
  type: actionTypes.PRE_FETCH_LOADING,
});

/**
 * Updates the search bar state (search by title).
 */
export const setTitle = (title: string): SyncReduxAction<{ title: string }> => ({
  type: actionTypes.SET_TITLE,
  title,
});

/**
 * Action for updating all filters
 */
export const setFilter = (filter?: AnyFilter): SyncReduxAction<{ filter?: AnyFilter }> => ({
  type: actionTypes.SET_FILTER,
  filter,
});

/**
 * Resets the filters and the search field
 */
export const resetAllFilters = () => ({
  type: actionTypes.RESET_FILTERS,
});

/**
 * Updates how the table is sorted.
 */
export const setSort = (sort: AnySort): SyncReduxAction<{ sort: AnySort }> => ({
  type: actionTypes.SET_SORT,
  sort,
});

/**
 * Action for updating the pagination page.
 */
export const setPage = (page: number): SyncReduxAction<{ page: number }> => ({
  type: actionTypes.SET_PAGE,
  page,
});

/**
 * Action for updating the pagination count.
 */
export const setCount = (count: number): SyncReduxAction<{ count: number }> => ({
  type: actionTypes.SET_COUNT,
  count,
});

/**
 * Action creator for the FETCH_PROJECTS action (projects).
 * Note that the fetchProjects thunk below is what handles passing the
 * query to this action, so this action isn't dispatched directly.
 */
const fetchProjectsAction = (query: ProjectsQuery): ReduxAction<ProjectsResponse> => ({
  type: actionTypes.FETCH_PROJECTS,
  meta: { timestamp: Date.now() },
  payload: async () => get<ProjectsResponse>(
    ENDPOINTS.PROJECTS,
    { ...query, filter: undefined, ...query.filter },
  ),
});

/**
 * Action creator for the FETCH_PROJECTS action (releaseGroups).
 * Note that the fetchProjects thunk below is what handles passing the
 * query to this action, so this action isn't dispatched directly.
 */
const fetchReleaseGroupsAction = (query: ProjectsQuery): ReduxAction<ReleaseGroupsResponse> => ({
  type: actionTypes.FETCH_PROJECTS,
  meta: { timestamp: Date.now() },
  payload: async () => get<ReleaseGroupsResponse>(
    ENDPOINTS.RELEASE_GROUPS,
    { ...query, filter: undefined, ...query.filter },
  ),
});

/**
 * This thunk is responsible for taking the current state and massaging
 * it into the fetchProjects query. This is dispatched from the useFetchProjectsAction
 * hook, which is responsible for the debouncing of this action.
 */
export const fetchProjects = (): ThunkAction<void, ReduxStore, never, AnyAction> =>
  (dispatch, getState) => {
    // Get the latest copy of the state, and use the selector to grab just the projectsV2 store.
    const state = getState();
    // Check which category of projects we are fetching (projects / release groups)
    const category = getCategory(state);

    const query = getFetchProjectsQuery(state);

    query.count = _.clamp(query.count, 0, COUNT_MAX_FE);

    // Dispatch the actual async action which makes the request.
    if (isProjects(category)) {
      dispatch(fetchProjectsAction(query));
    } else if (isSharedProjects(category)) {
      dispatch(fetchProjectsAction({ ...query, filter: { ...query.filter, onlyIncludeSharedProjects: true } }));
    } else {
      dispatch(fetchReleaseGroupsAction(query));
    }
  };

export const fetchOrganzationLabels = (): ReduxAction<LabelsResponse> => ({
  type: actionTypes.FETCH_ORGANIZATION_LABELS,
  meta: { timestamp: Date.now() },
  payload: async () => get<LabelsResponse>(ENDPOINTS.ORGANIZATION_LABELS),
});

/**
 * Action for toggling the bulk selection checkbox.
 */
export const toggleSelectAllOnPage = (): AnyAction => ({
  type: actionTypes.TOGGLE_SELECT_ALL_ON_PAGE,
});

/**
 * Action for setting selectedProjects to 'all', across all pages.
 */
export const selectAllProjects = (): AnyAction => ({
  type: actionTypes.SELECT_ALL_PROJECTS,
});

/**
 * Action for selecting a project.
 */
export const selectProject = (projectId: string | number): SyncReduxAction<{ projectId: string | number }> => ({
  type: actionTypes.SELECT_PROJECT,
  projectId,
});

/**
 * Action for deselecting a project.
 */
export const deselectProject = (projectId: string | number): SyncReduxAction<{ projectId: string | number }> => ({
  type: actionTypes.DESELECT_PROJECT,
  projectId,
});

/**
 * Action for clearing the selection in the ProjectsBulkSelectionBanner
 */
export const clearSelection = (): AnyAction => ({
  type: actionTypes.CLEAR_SELECTION,
});

/**
 * Handles hydrating the state based on route changes + URL
 * query params when the page initially loads.
 *
 * @param {HydrateStateQuery} query - A partial state, to be hydrated.
 */
export const hydrateState = (query: HydrateStateQuery): SyncReduxAction<{ query: HydrateStateQuery }> => ({
  type: actionTypes.HYDRATE_STATE,
  query,
});

export const fetchCategoriesSummary = (): ReduxAction<CategoriesSummary> => ({
  type: actionTypes.FETCH_CATEGORIES_SUMMARY,
  meta: { timestamp: Date.now() },
  payload: async () => get<CategoriesSummary>(ENDPOINTS.CATEGORIES_SUMMARY),
});

export const setCategory = (category: ProjectCategory): SyncReduxAction<{ category: ProjectCategory }> => ({
  type: actionTypes.SET_CATEGORY,
  category,
});

export const deleteSelectedProjects = (): ThunkAction<void, ReduxStore, never, AnyAction> =>
  (dispatch, getState) => {
    const state = getState();
    const category = getCategory(state);
    const endpoint = isProjects(category) ? ENDPOINTS.PROJECTS : ENDPOINTS.RELEASE_GROUPS;
    const key = isProjects(category) ? 'locators' : 'ids';

    const locators = getSelectedProjectLocators(state);
    if (locators === 'all') {
      const query = removeEmptyAttributesFromObject(getProjectsFilterQuery(state));
      if (_.isEmpty(query)) {
        return dispatch({
          type: actionTypes.DELETE_PROJECTS,
          payload: async () => API.remove<void, void>(`${endpoint}?${key}=all`),
        });
      }
      return dispatch({
        type: actionTypes.DELETE_PROJECTS,
        payload: async () => API.remove<void, void>(`${endpoint}?${qs.stringify(query)}`),
      });
    }

    return dispatch({
      type: actionTypes.DELETE_PROJECTS,
      payload: async () => API.remove<void, void>(`${endpoint}?${qs.stringify({ [key]: getSelectedProjectLocators(state) })}`),
    });
  };

// These actions only get called when a single team is selected.
export const removeSelectedProjectsFromTeam = (): ThunkAction<void, ReduxStore, never, AnyAction> =>
  (dispatch, getState) => {
    const state = getState();
    const locators = getSelectedProjectLocators(state);
    if (locators === 'all') {
      const query = removeEmptyAttributesFromObject(getProjectsFilterQuery(state));
      if (_.isEmpty(query)) {
        return dispatch({
          type: actionTypes.REMOVE_FROM_TEAM,
          payload: async () =>
            API.put<TeamReqBody, void>(`${ENDPOINTS.TEAMS}/${state.teams.teamSelection[0]}/projects`, {
              action: 'remove',
              projects: 'all',
            }),
        });
      }
      return dispatch({
        type: actionTypes.REMOVE_FROM_TEAM,
        payload: async () =>
          API.put<TeamReqBody, void>(`${ENDPOINTS.TEAMS}/${state.teams.teamSelection[0]}/projects`, {
            action: 'remove',
            filters: query,
          }),
      });
    }

    return dispatch({
      type: actionTypes.REMOVE_FROM_TEAM,
      payload: async () =>
        API.put<TeamReqBody, void>(`${ENDPOINTS.TEAMS}/${state.teams.teamSelection[0]}/projects`, {
          action: 'remove',
          projects: locators,
        }),
    });
  };

//  This action is used to re-fetch the summaries after deleting projects,
//  since the counts will be updated when this action happens.
export const refetchProjects = (): ThunkAction<void, ReduxStore, never, AnyAction> =>
  (dispatch) => {
    dispatch(fetchProjects());
    dispatch(fetchCategoriesSummary());
    dispatch(clearSelection());
  };

const bulkApplyLabelAction = (query: BulkProjectLabelQuery) => ({
  type: actionTypes.BULK_APPLY_LABEL,
  payload: async () => API.put<EmptyObject, BulkActionResponse>(`${ENDPOINTS.BULK_LABEL}?${qs.stringify(query)}`, {}),
});

/**
 * Given a label ID, apply a label to many projects based on the current Projects V2 Store state. This will respect
 * filters.
 * @param labelId - The ID of the label to apply.
 * @param addToast - The hook for adding a toast. Providing it here let's use display the toast before the reloading has
 * finished but after the changes have been made.
 */
export const bulkApplyLabel =
  (labelId: number, addToast: (toast: NewToast) => void): ThunkAction<void, ReduxStore, never, AnyAction> =>
    async (dispatch, getState) => {
      const state = getState();

      const locators = getSelectedProjectLocators(state);

      let action: ReturnType<typeof bulkApplyLabelAction>;
      if (locators === 'all') {
        const query = removeEmptyAttributesFromObject<Omit<ProjectsActionQuery, 'locators'>>(
          getProjectsFilterQuery(state),
        );
        if (_.isEmpty(query)) {
          action = bulkApplyLabelAction({ locators: 'all', labelId });
        } else {
          action = bulkApplyLabelAction({ ...query, labelId, locators: undefined });
        }
      } else {
        action = bulkApplyLabelAction({ locators: getSelectedProjectLocators(state), labelId });
      }

      const results = (await dispatch(action)) as any as AwaitedThunkResponse<BulkActionResponse>;

      if (results.value.data.created) {
        addToast({
          type: 'success',
          content: `Added label to ${results.value.data.created} ${plural(results.value.data.created, 'project')}.`,
        });
      }

      const errMessages = Object.values(results.value.data.errors);
      if (errMessages.length > 5) {
        addToast({
          type: 'error',
          content: `Failed to add label to ${errMessages.length} ${plural(results.value.data.created, 'project')}.`,
        });
      } else {
        errMessages.forEach((err) => addToast({ type: 'error', content: err }));
      }
    };

/**
 * Given a policy ID, apply a policy to many projects based on the current Projects V2 Store state. This will respect
 * filters.
 * @param policyId - The ID of the policy to apply.
 * @param addToast - The hook for adding a toast. Providing it here let's use display the toast before the reloading has
 * finished but after the changes have been made.
 */
export const bulkApplyPolicy =
  (policyId: number, addToast: (toast: NewToast) => void): ThunkAction<void, ReduxStore, never, AnyAction> =>
    async (dispatch, getState) => {
      const state = getState();
      const category = getCategory(state);
      const endpoint = isProjects(category) ? ENDPOINTS.PROJECTS : ENDPOINTS.RELEASE_GROUPS;
      const key = isProjects(category) ? 'locators' : 'ids';

      const locators = getSelectedProjectLocators(state);

      let finalQuery: Record<string, any> = {};
      if (locators === 'all') {
        const query = removeEmptyAttributesFromObject<Omit<ProjectsActionQuery, 'locators'>>(
          getProjectsFilterQuery(state),
        );
        if (_.isEmpty(query)) {
          finalQuery = { [key]: 'all', policyId };
        } else {
          finalQuery = { ...query, policyId, [key]: undefined };
        }
      } else {
        finalQuery = { [key]: getSelectedProjectLocators(state), policyId };
      }

      const results = (await dispatch({
        type: actionTypes.BULK_APPLY_POLICY,
        payload: async () => API.put<EmptyObject, BulkActionResponse>(`${endpoint}/policy?${qs.stringify(finalQuery)}`, {}),
      })) as any as AwaitedThunkResponse<BulkActionResponse>;

      if (results.value.data.created) {
        addToast({
          type: 'success',
          content: `Assigned policy to ${results.value.data.created} ${plural(results.value.data.created, ProjectCategoryName[category])}.`,
        });
      }

      const errMessages = Object.values(results.value.data.errors);
      if (errMessages.length > 5) {
        addToast({
          type: 'error',
          content: `Failed to add label to ${errMessages.length} ${plural(results.value.data.created, ProjectCategoryName[category])}.`,
        });
      } else {
        errMessages.forEach((err) => addToast({ type: 'error', content: err }));
      }
    };
